// Third party
import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';

// Components
import UserDropdown from '../navigation/UserDropdown';
import { Button } from '@capintel/component-lib/button';
import { AnnouncementBell } from '@/views/containers/announcements';

// Utils
import { FormattedMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';

// Actions
import { logOpenHelpCenter } from '@/redux/modules/usage-log-deprecated';
import { ReduxState } from '@/redux/modules/types';
import Locale from '@/types/locale';
import User from '@/types/user';
import useThemeVersion from '@/hooks/useThemeVersion';
import LegacyLinkHeader from './LegacyLinkHeader';
import useScreenSize from '@/hooks/useScreenSize';

type StateProps = {
  locale: Locale;
};

type DispatchProps = {
  logOpenHelpCenter: () => Promise<void>;
};

type Props = RouteComponentProps &
  DispatchProps &
  StateProps & {
    user: User;
    leftItems?: JSX.Element | Iterable<JSX.Element> | React.ReactNode[];
    middleItems?: JSX.Element | Iterable<JSX.Element>;
    rightItems?: JSX.Element | Iterable<JSX.Element>;
  };

const LinkHeaderV2 = (props: Props) => {
  const { isDesktop, isMobile } = useScreenSize();
  const showResponsiveView = userHasPermissionEnabled(props.user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) && !isDesktop;

  const openHelpLink = () => {
    props.logOpenHelpCenter();
    /** @todo This is temporarily hardcoded for release 3.94, should be removed in favor of an enterprise config on CONN-3067 */
    const category = props.locale.startsWith('en-')
      ? 'categories/4408872951955-Link-Tutorials'
      : props.locale.startsWith('fr-')
        ? 'categories/4408872951955-FAQ-et-Dépannage'
        : '';
    window.open(`${import.meta.env.REACT_APP_HELP_SITE_URL}/${props.locale.toLocaleLowerCase().replace('en-ca', 'en-us')}/${category}`, '_blank');
  };

  const { leftItems, middleItems, rightItems, user } = props;
  return (
    <header className="link-header-v2">
      <div className="link-header-container">
        <div className="left-items">{leftItems}</div>
        <div className="middle-items">{middleItems}</div>
        <div className="right-items">
          {rightItems && <div className="header-buttons">{rightItems}</div>}
          {(!userHasPermission(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) || (!isMobile && userHasPermission(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY))) && (
            <>
              <Button variant="secondary" appearance="text" icon="help-outline" onClick={openHelpLink} />

              {userHasPermission(user, PERMISSIONS.EXTERNAL_NOTIFICATION_CENTER) && <Button variant="secondary" appearance="text" icon="notification-bell" />}
              {userHasPermission(user, PERMISSIONS.NOTIFICATION_CENTER) && <AnnouncementBell />}
            </>
          )}
          {showResponsiveView ? (
            <Button variant="secondary" appearance="text" icon="more-vertical" />
          ) : (
            <UserDropdown user={user} history={props.history} location={props.location} match={props.match} />
          )}
        </div>
      </div>
    </header>
  );
};

const LinkHeader = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  return isLightVersion ? <LinkHeaderV2 {...props} /> : <LegacyLinkHeader {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  locale: state.intl.locale,
});

export default withRouter(
  connect(mapStateToProps, {
    logOpenHelpCenter,
  })(LinkHeader),
);
